import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { findDeleted } from 'api/UserController';
import type {
  IUserFindDeletedQuery,
  IUserFindDeletedResponse,
} from '@/app-package';
import { ROLES, UserRoles, userRolesToLabelMap } from '@bb/users';
import { useDebounce } from 'hooks';
import errorReporter from '@app/errorReporter';
import { message } from 'components';

interface useGetDeletedUsersProps {
  shouldFetch?: boolean;
  locations?: string[];
  searchText?: string;
  roleFilters?: (keyof typeof UserRoles)[];
}

export type DeletedUser = IUserFindDeletedResponse[0] & { name: string };

const useGetDeletedUsers = ({
  shouldFetch = true,
  locations,
  searchText,
  roleFilters,
}: useGetDeletedUsersProps) => {
  const history = useHistory();
  const debouncedSearchText = useDebounce(searchText);
  const [isLoading, setIsLoading] = useState(false);
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [existingRoles, setExistingRoles] = useState<
    (keyof typeof UserRoles)[]
  >([]);

  useEffect(() => {
    if (!shouldFetch) return;
    let active = true;
    const fetchDeletedUsers = async () => {
      try {
        setIsLoading(true);
        const params: IUserFindDeletedQuery = {};
        if (locations?.length) params.locations = locations;
        if (debouncedSearchText) params.userName = debouncedSearchText;
        if (roleFilters?.length)
          params.roles = roleFilters.map(key => UserRoles[key]);
        const data = await findDeleted(params);
        if (!active) return;
        setDeletedUsers(
          data.map((user: IUserFindDeletedResponse[0]) => ({
            ...user,
            name: `${user.firstName} ${user.lastName}`,
          })),
        );

        const searchParams = new URLSearchParams(history.location.search);

        if (locations?.length === 0 && roleFilters?.length === 0) {
          const roleSet = new Set<keyof typeof UserRoles>();

          data.forEach((user: IUserFindDeletedResponse[0]) => {
            roleSet.add(<keyof typeof UserRoles>userRolesToLabelMap[user.role]);
          });
          const newExistingRoles = Array.from(roleSet)
            .map(key => UserRoles[key])
            .sort((a, b) => ROLES[a] - ROLES[b])
            .join('#');

          searchParams.set('deletedUserExistingRoles', newExistingRoles);
        }

        setExistingRoles(
          (searchParams.get('deletedUserExistingRoles') ?? '')
            .split('#')
            .map(
              (key: UserRoles) => userRolesToLabelMap[key],
            ) as (keyof typeof UserRoles)[],
        );

        if (searchParams.get('tabKey') === 'deleted') {
          searchParams.delete('locations[]');
          if (locations?.length) {
            locations.forEach(location =>
              searchParams.append('locations[]', location),
            );
          }
          searchParams.delete('roleFilters[]');
          if (roleFilters?.length) {
            roleFilters.forEach(roleFilter =>
              searchParams.append('roleFilters[]', UserRoles[roleFilter]),
            );
          }
          searchParams.set('userName', debouncedSearchText);
          history.replace({
            pathname: history.location.pathname,
            search: searchParams.toString(),
          });
        }
      } catch (err) {
        errorReporter.error(err);
        message.error('Error loading deleted users');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDeletedUsers();

    return () => {
      active = false;
    };
  }, [shouldFetch, locations, debouncedSearchText, history, roleFilters]);

  return {
    deletedUsers,
    isLoading,
    deletedUsersCount: deletedUsers.length,
    existingDeletedUserRoles: existingRoles,
  };
};

export default useGetDeletedUsers;
